import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Loader2, Search, Mail, CheckCircle, AlertCircle, RefreshCw, UserCheck, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getStorageUrl } from "@/lib/storage";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
interface UserCheckResult {
  exists: boolean;
  user_id?: string;
  email?: string;
  email_confirmed?: boolean;
  email_confirmed_at?: string;
  created_at?: string;
  last_sign_in_at?: string;
  profile?: {
    full_name: string;
    avatar_url: string | null;
    role: string;
    is_published: boolean;
  };
}

interface ActionResult {
  success: boolean;
  message: string;
  error?: string;
}

export function UserEmailVerificationManager() {
  const queryClient = useQueryClient();
  const [searchEmail, setSearchEmail] = useState("");
  const [searchedEmail, setSearchedEmail] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  
  // Check user status
  const { data: userStatus, isLoading: checkingUser, refetch: refetchUser } = useQuery({
    queryKey: ["admin-user-email-check", searchedEmail],
    queryFn: async (): Promise<UserCheckResult | null> => {
      if (!searchedEmail) return null;
      
      const { data, error } = await supabase.functions.invoke("admin-manage-user-email", {
        body: { action: "check-user", email: searchedEmail },
      });
      
      if (error) {
        console.error("Error checking user:", error);
        throw error;
      }
      
      return data;
    },
    enabled: !!searchedEmail,
  });
  
  // Fetch recent verification actions
  const { data: recentActions } = useQuery({
    queryKey: ["admin-email-verification-logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_activity_logs")
        .select("*")
        .in("action_type", ["manual_email_confirm", "resend_verification_email", "delete_orphan_auth_user"])
        .order("created_at", { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data;
    },
  });
  
  // Resend verification email
  const resendMutation = useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.functions.invoke("admin-manage-user-email", {
        body: { action: "resend-verification", email },
      });
      
      if (error) throw error;
      return data as ActionResult;
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message);
        queryClient.invalidateQueries({ queryKey: ["admin-email-verification-logs"] });
      } else {
        toast.error(data.message || "Failed to resend verification email");
      }
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message}`);
    },
  });
  
  // Manually confirm email
  const confirmMutation = useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.functions.invoke("admin-manage-user-email", {
        body: { action: "confirm-user", email },
      });
      
      if (error) throw error;
      return data as ActionResult;
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message);
        refetchUser();
        queryClient.invalidateQueries({ queryKey: ["admin-email-verification-logs"] });
      } else {
        toast.error(data.message || "Failed to confirm user email");
      }
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message}`);
    },
  });
  
  // Delete orphan auth user
  const deleteOrphanMutation = useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.functions.invoke("admin-manage-user-email", {
        body: { action: "delete-orphan-user", email },
      });
      
      if (error) throw error;
      return data as ActionResult;
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message);
        setShowDeleteDialog(false);
        setDeleteConfirmEmail("");
        handleClear();
        queryClient.invalidateQueries({ queryKey: ["admin-email-verification-logs"] });
      } else {
        toast.error(data.message || "Failed to delete auth user");
      }
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchEmail.trim()) {
      setSearchedEmail(searchEmail.trim().toLowerCase());
    }
  };
  
  const handleClear = () => {
    setSearchEmail("");
    setSearchedEmail("");
  };

  const handleDeleteOrphan = () => {
    if (deleteConfirmEmail.toLowerCase() === searchedEmail.toLowerCase()) {
      deleteOrphanMutation.mutate(searchedEmail);
    } else {
      toast.error("Email doesn't match. Please type the exact email to confirm.");
    }
  };
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            User Email Verification Manager
          </CardTitle>
          <CardDescription>
            Check user email verification status, resend confirmation emails, or manually confirm users
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Enter email address to check..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={!searchEmail.trim() || checkingUser}>
              {checkingUser ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span className="ml-2">Check</span>
            </Button>
            {searchedEmail && (
              <Button type="button" variant="outline" onClick={handleClear}>
                Clear
              </Button>
            )}
          </form>
          
          {/* User Status Results */}
          {searchedEmail && (
            <div className="space-y-4">
              {checkingUser ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : userStatus?.exists ? (
                <div className="rounded-lg border bg-card p-4 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={getStorageUrl("avatars", userStatus.profile?.avatar_url) || undefined} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {userStatus.profile?.full_name?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold">{userStatus.profile?.full_name || "No profile"}</p>
                        <p className="text-sm text-muted-foreground">{userStatus.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="capitalize">
                            {userStatus.profile?.role || "No role"}
                          </Badge>
                          {userStatus.profile?.is_published ? (
                            <Badge variant="default">Published</Badge>
                          ) : (
                            <Badge variant="secondary">Not Published</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {userStatus.email_confirmed ? (
                        <Badge variant="default" className="bg-green-600">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Email Verified
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Email NOT Verified
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-muted-foreground">Created</Label>
                      <p>{userStatus.created_at ? format(new Date(userStatus.created_at), "PPp") : "Unknown"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Last Sign In</Label>
                      <p>{userStatus.last_sign_in_at ? format(new Date(userStatus.last_sign_in_at), "PPp") : "Never"}</p>
                    </div>
                    {userStatus.email_confirmed_at && (
                      <div>
                        <Label className="text-muted-foreground">Email Confirmed At</Label>
                        <p>{format(new Date(userStatus.email_confirmed_at), "PPp")}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    {!userStatus.email_confirmed && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => resendMutation.mutate(searchedEmail)}
                          disabled={resendMutation.isPending}
                        >
                          {resendMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-2" />
                          )}
                          Resend Verification Email
                        </Button>
                        <Button
                          onClick={() => confirmMutation.mutate(searchedEmail)}
                          disabled={confirmMutation.isPending}
                        >
                          {confirmMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <UserCheck className="h-4 w-4 mr-2" />
                          )}
                          Manually Confirm Email
                        </Button>
                      </>
                    )}
                    
                    {/* Show delete button only when user exists but has no profile */}
                    {!userStatus.profile && (
                      <Button
                        variant="destructive"
                        onClick={() => setShowDeleteDialog(true)}
                        disabled={deleteOrphanMutation.isPending}
                      >
                        {deleteOrphanMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-2" />
                        )}
                        Delete Auth User (Orphan)
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border bg-muted/50 p-6 text-center">
                  <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="font-medium">No user found</p>
                  <p className="text-sm text-muted-foreground">
                    No user exists with email: {searchedEmail}
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Recent Actions Log */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Verification Actions</CardTitle>
        </CardHeader>
        <CardContent>
          {recentActions && recentActions.length > 0 ? (
            <ScrollArea className="h-48">
              <div className="space-y-2">
                {recentActions.map((log) => (
                  <div key={log.id} className="flex items-center justify-between text-sm p-2 rounded border bg-card">
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={log.action_type === "delete_orphan_auth_user" ? "destructive" : "outline"} 
                        className="text-xs"
                      >
                        {log.action_type === "manual_email_confirm" 
                          ? "Confirmed" 
                          : log.action_type === "delete_orphan_auth_user"
                          ? "Deleted"
                          : "Resent"}
                      </Badge>
                      <span className="text-muted-foreground">{log.target_user_email}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(log.created_at), "PP p")}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No recent actions</p>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Permanently Delete Auth User?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                This will permanently delete the auth record for <strong>{searchedEmail}</strong>. 
                This action cannot be undone.
              </p>
              <p>
                After deletion, this email can be used to register as a new user.
              </p>
              <div className="pt-2">
                <Label htmlFor="confirm-email">Type the email to confirm:</Label>
                <Input
                  id="confirm-email"
                  placeholder={searchedEmail}
                  value={deleteConfirmEmail}
                  onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                  className="mt-1"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmEmail("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOrphan}
              disabled={deleteConfirmEmail.toLowerCase() !== searchedEmail.toLowerCase() || deleteOrphanMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteOrphanMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
